import {Box, IconButton, Typography} from '@mui/material';
import {t} from 'i18next';
import './ProtocolHeader.scss';
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ArrButton from '../../../../../common/arr-button/ArrButton';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {useLocation, useNavigate} from 'react-router';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../../../redux/store';
import {
  ProtocolStatus,
  resetProtocol,
  setInitialProtocol,
  setProtocolIds,
  setProtocolName,
  setProtocolStatus,
  showToast,
} from '../../../../../../redux/slice/aiReviewSliceV2';
import {
  useCreateProtocolIntelligence,
  useCreateProtocolVersion,
  useUpdateProtocolVersionDetails,
  useValidateProtocolName,
  useUpdateProtocolName,
} from '../../../../../../hooks/ai/review/useReview';
import {
  ICreateProtocolIntelligenceResponse,
  ICreateProtocolVersionResponse,
} from '../../../../../../api/types/aiReview.type';
import {
  areProtocolFormsEqual,
  buildCreateProtocolPayload,
  buildCreateProtocolVersionPayload,
  buildProtocolDirtyPayload,
  computeNextVersionName,
  resolvePersistedProtocolIds,
} from '../../../protocolIntelligenceV2/utils/protocolVersionPayload';
import {ToastType} from '../../../../../../shared/constants';
import ArrTextField from '../../../../../common/arr-text-field/ArrTextField';
import {
  PROTOCOL_NAME_MAX_LENGTH,
  ProtocolNameValidationError,
  getProtocolNameValidationError,
  getProtocolNameValidationMessage,
  isDuplicateNameResponse,
} from '../create-protocol-modal/CreateNewProtocolModal';
import ExitProtocolModal from './exit-protocol-modal/ExitProtocolModal';
import {
  navigateToPendingOrProtocolTab,
  navigateToProtocolTabLanding,
} from './exit-protocol-modal/protocolExitNavigation';
import {PROTOCOL_INTELLIGENCE_ROUTE} from '../../../utils';
import {ReactComponent as ClearIcon} from '../../../../../../assets/images/icon-close.svg';

const DRAFT_SAVE_SOURCE = {
  Header: 'header',
  Exit: 'exit',
} as const;

type DraftSaveSource =
  (typeof DRAFT_SAVE_SOURCE)[keyof typeof DRAFT_SAVE_SOURCE];

const normalizeVersionLabel = (version?: string | null): string => {
  const trimmed = version?.trim();
  if (!trimmed) {
    return 'V1';
  }
  return /^v/i.test(trimmed) ? trimmed : `V${trimmed}`;
};

export type ProtocolHeaderLocationState =
  | string
  | {
      protocolName?: string;
      protocolId?: number;
      versionId?: number;
      status?: ProtocolStatus | string;
      isApproved?: boolean;
      latestVersion?: string;
      versions?: Array<{
        versionId: number;
        name: string;
        status: ProtocolStatus;
      }>;
      protocolVersionId?: number;
    }
  | null;

export interface ProtocolHeaderProps {
  isSavingDisabled?: boolean;
  status?: ProtocolStatus;
  protocolName?: string;
  existingProtocolNames?: string[];
  onVersionHistoryClick?: () => void;
  onReviseClick?: () => void;
  /** When true, draft header shows Cancel for unsaved revise flow. */
  isUnsavedRevision?: boolean;
  /** Parent approved version id for POST create-version (AVR-21867). */
  revisionParentVersionId?: number | null;
  /** Opens Discard Revision modal (unsaved revise Cancel). */
  onUnsavedRevisionCancel?: () => void;
  /** Called after successful Save Draft or Approve so parent can stay on page / switch view. */
  onRevisionPersisted?: (
    data: {protocolId: number; versionId: number},
    nextStatus: ProtocolStatus
  ) => void;
  /** Rollback to prior approved when create-version POST fails. */
  onRevisionCreateFailed?: () => void;
  onProtocolNameSave?: (name: string) => void;
  showExitModal: boolean;
  setShowExitModal: (open: boolean) => void;
  pendingPath: string | null;
  setPendingPath: (path: string | null) => void;
  onExitDiscard?: (versionId?: number | null) => void;
  onExitSaveDraft?: (versionId?: number | null) => void;
  /** When set, Discard/Save in the exit modal switch versions instead of leaving. */
  pendingSwitchVersionId?: number | null;
  trailingAction?: React.ReactNode;
  /** When false, hide Version History (approved header). Default true. */
  showVersionControls?: boolean;
}

const getLocationProtocolName = (
  state: ProtocolHeaderLocationState
): string => {
  if (typeof state === 'string') {
    return state;
  }
  return state?.protocolName ?? '';
};

const getLocationProtocolId = (
  state: ProtocolHeaderLocationState
): number | undefined => {
  if (
    state &&
    typeof state === 'object' &&
    typeof state.protocolId === 'number'
  ) {
    return state.protocolId;
  }
  return undefined;
};

const getLocationVersionId = (
  state: ProtocolHeaderLocationState
): number | undefined => {
  if (state && typeof state === 'object') {
    if (typeof state.versionId === 'number') {
      return state.versionId;
    }
    if (typeof state.protocolVersionId === 'number') {
      return state.protocolVersionId;
    }
  }
  return undefined;
};

const getApiErrorStatus = (error: unknown): number | undefined => {
  const apiError = error as {status?: number; response?: {status?: number}};
  return apiError?.response?.status ?? apiError?.status;
};

const getLocationStatus = (
  state: ProtocolHeaderLocationState
): ProtocolStatus | undefined => {
  if (state && typeof state === 'object' && typeof state.status === 'string') {
    const normalized = state.status.toLowerCase();
    if (normalized === 'approved' || normalized === 'draft') {
      return normalized;
    }
  }
  return undefined;
};

const getLocationIsApproved = (
  state: ProtocolHeaderLocationState
): boolean | undefined => {
  if (
    state &&
    typeof state === 'object' &&
    typeof state.isApproved === 'boolean'
  ) {
    return state.isApproved;
  }
  return undefined;
};

const isApprovedStatus = (value?: string | null) =>
  value?.trim().toLowerCase() === 'approved';

const hasFilledText = (value?: string | null) => Boolean(value?.trim());

/** Approve requires executive_summary, master_relevance_standard, and one complete issue code. */
export const hasMandatoryApproveFields = (protocol: {
  caseContext?: {executive_summary?: string};
  relevanceCriteria?: {master_relevance_standard?: string};
  issueCoding?: {
    issue_codes?: Array<{
      code_id?: string;
      code_name?: string;
      category?: string;
      priority?: string;
      definition?: string;
    }>;
  };
}): boolean => {
  if (!hasFilledText(protocol.caseContext?.executive_summary)) {
    return false;
  }
  if (!hasFilledText(protocol.relevanceCriteria?.master_relevance_standard)) {
    return false;
  }

  const issueCodes = protocol.issueCoding?.issue_codes ?? [];
  return issueCodes.some(
    (code) =>
      hasFilledText(code.code_id) &&
      hasFilledText(code.code_name) &&
      hasFilledText(code.category) &&
      hasFilledText(code.priority) &&
      hasFilledText(code.definition)
  );
};

const ProtocolHeader: React.FC<ProtocolHeaderProps> = ({
  isSavingDisabled = false,
  status: statusProp,
  protocolName: protocolNameProp,
  existingProtocolNames = [],
  onVersionHistoryClick,
  onReviseClick,
  isUnsavedRevision = false,
  revisionParentVersionId = null,
  onUnsavedRevisionCancel,
  onRevisionPersisted,
  onRevisionCreateFailed,
  onProtocolNameSave,
  showExitModal,
  setShowExitModal,
  pendingPath,
  setPendingPath,
  onExitDiscard,
  onExitSaveDraft,
  pendingSwitchVersionId = null,
  trailingAction,
  showVersionControls = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const locationState = location.state as ProtocolHeaderLocationState;

  const protocol = useSelector((state: RootState) => state.aiReviewV2.protocol);
  const initialProtocol = useSelector(
    (state: RootState) => state.aiReviewV2.initialProtocol
  );
  const protocolVersions = useSelector(
    (state: RootState) => state.aiReviewV2.protocolVersions.versions
  );

  const hasUnsavedChanges = !areProtocolFormsEqual(protocol, initialProtocol);
  const persistedVersionId =
    protocol.protocolVersionId ??
    initialProtocol.protocolVersionId ??
    getLocationVersionId(locationState);
  const trimmedProtocolName = protocol?.protocolName?.trim() || '';
  const [submittingStatus, setSubmittingStatus] =
    useState<ProtocolStatus | null>(null);
  const draftSaveSourceRef = useRef<DraftSaveSource>(DRAFT_SAVE_SOURCE.Header);
  const pendingSwitchVersionIdRef = useRef<number | null>(null);
  if (showExitModal && pendingSwitchVersionId != null) {
    pendingSwitchVersionIdRef.current = pendingSwitchVersionId;
  }
  if (!showExitModal) {
    pendingSwitchVersionIdRef.current = null;
  }
  const resolvedSwitchVersionId =
    pendingSwitchVersionId ?? pendingSwitchVersionIdRef.current;

  const resolveDraftVersionLabel = useCallback(
    (versionId?: number | null) => {
      if (versionId != null) {
        const match = protocolVersions.find(
          (version) => version.versionId === versionId
        );
        if (match?.name) {
          return normalizeVersionLabel(match.name);
        }
      }

      const latestFromNav =
        locationState && typeof locationState === 'object'
          ? locationState.latestVersion
          : undefined;
      return normalizeVersionLabel(latestFromNav);
    },
    [locationState, protocolVersions]
  );

  const showDraftSuccessToast = useCallback(
    (versionId?: number | null, versionLabel?: string | null) => {
      dispatch(
        showToast({
          title: t('ai.review.protocolIntelligence.draftToastTitle'),
          message: t('ai.review.protocolIntelligence.draftToastDesc', {
            value: protocol.protocolName?.trim() || '',
            version: versionLabel?.trim()
              ? normalizeVersionLabel(versionLabel)
              : resolveDraftVersionLabel(versionId),
          }),
          status: [ToastType.Success],
        })
      );
    },
    [dispatch, protocol.protocolName, resolveDraftVersionLabel]
  );

  const leaveAfterExitDraftSave = useCallback(() => {
    if (resolvedSwitchVersionId != null) {
      onExitSaveDraft?.(resolvedSwitchVersionId);
      return;
    }
    const path = pendingPath;
    setPendingPath(null);
    setShowExitModal(false);
    dispatch(resetProtocol());
    navigateToPendingOrProtocolTab(navigate, path);
  }, [
    dispatch,
    navigate,
    onExitSaveDraft,
    pendingPath,
    resolvedSwitchVersionId,
    setPendingPath,
    setShowExitModal,
  ]);

  const afterDraftPersisted = useCallback(
    (
      options: {navigateBackOnHeader: boolean},
      versionId?: number | null,
      versionLabel?: string | null
    ) => {
      showDraftSuccessToast(versionId, versionLabel);
      const source = draftSaveSourceRef.current;
      draftSaveSourceRef.current = DRAFT_SAVE_SOURCE.Header;
      if (source === DRAFT_SAVE_SOURCE.Exit) {
        leaveAfterExitDraftSave();
        return;
      }
      if (options.navigateBackOnHeader) {
        navigate(-1);
      }
    },
    [leaveAfterExitDraftSave, navigate, showDraftSuccessToast]
  );

  const status =
    statusProp ??
    getLocationStatus(locationState) ??
    protocol.status ??
    'draft';
  const isApproved =
    statusProp !== undefined
      ? isApprovedStatus(statusProp)
      : (getLocationIsApproved(locationState) ?? isApprovedStatus(status));

  const resolvedProtocolName =
    protocolNameProp ??
    (getLocationProtocolName(locationState) || protocol.protocolName);

  const [isEditingName, setIsEditingName] = useState(false);
  const [protocolNameValue, setProtocolNameValue] =
    useState(resolvedProtocolName);
  const [originalProtocolName, setOriginalProtocolName] =
    useState(resolvedProtocolName);
  const [validationError, setValidationError] =
    useState<ProtocolNameValidationError | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const {mutateAsync: validateName, isPending: isValidatingName} =
    useValidateProtocolName();
  const {mutateAsync: updateName, isPending: isUpdatingName} =
    useUpdateProtocolName();
  const isSavingName = isValidatingName || isUpdatingName;

  const namesForDuplicateCheck = useMemo(
    () =>
      existingProtocolNames.filter(
        (name) =>
          name.trim().toLowerCase() !==
          originalProtocolName.trim().toLowerCase()
      ),
    [existingProtocolNames, originalProtocolName]
  );

  const helperText = useMemo(() => {
    const errorMessage = getProtocolNameValidationMessage(validationError);
    if (errorMessage) {
      return errorMessage;
    }
    return `${protocolNameValue.length}/${PROTOCOL_NAME_MAX_LENGTH}`;
  }, [protocolNameValue.length, validationError]);

  useEffect(() => {
    setProtocolNameValue(resolvedProtocolName);
    setOriginalProtocolName(resolvedProtocolName);
    setValidationError(null);
  }, [resolvedProtocolName]);

  useEffect(() => {
    if (isEditingName && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditingName]);

  const handleCreateSuccess = useCallback(
    (data: ICreateProtocolIntelligenceResponse, nextStatus: ProtocolStatus) => {
      const {protocolId: nextProtocolId, protocolVersionId: nextVersionId} =
        resolvePersistedProtocolIds(data, protocol, initialProtocol, {
          protocolId: getLocationProtocolId(locationState),
          protocolVersionId: getLocationVersionId(locationState),
        });

      if (nextProtocolId != null && nextVersionId != null) {
        dispatch(
          setProtocolIds({
            protocolId: nextProtocolId,
            protocolVersionId: nextVersionId,
          })
        );
      }
      dispatch(setProtocolStatus(nextStatus));
      dispatch(
        setInitialProtocol({
          ...protocol,
          ...(nextProtocolId != null ? {protocolId: nextProtocolId} : {}),
          ...(nextVersionId != null ? {protocolVersionId: nextVersionId} : {}),
          status: nextStatus,
        })
      );
      if (nextProtocolId != null && nextVersionId != null) {
        onRevisionPersisted?.(
          {protocolId: nextProtocolId, versionId: nextVersionId},
          nextStatus
        );
      }

      if (typeof location.state === 'string' && nextProtocolId != null && nextVersionId != null) {
        navigate(PROTOCOL_INTELLIGENCE_ROUTE, {
          replace: true,
          state: {
            protocolName: trimmedProtocolName || protocol.protocolName.trim(),
            protocolId: nextProtocolId,
            versionId: nextVersionId,
            status: nextStatus,
          },
        });
      }

      if (nextStatus === 'draft') {
        afterDraftPersisted({navigateBackOnHeader: false}, nextVersionId);
        return;
      }

      dispatch(
        showToast({
          title: t('common.success'),
          message: t(
            'ai.review.protocolIntelligence.protocolApprovedToastSuccess'
          ),
          status: [ToastType.Success],
        })
      );
    },
    [
      afterDraftPersisted,
      dispatch,
      initialProtocol,
      location.state,
      locationState,
      navigate,
      onRevisionPersisted,
      protocol,
      trimmedProtocolName,
    ]
  );

  const handleCreateError = useCallback(
    (nextStatus: ProtocolStatus) => {
      dispatch(
        showToast({
          title: t('common.error'),
          message:
            nextStatus === 'approved'
              ? t('ai.review.protocolIntelligence.protocolApprovedToastFailure')
              : t('ai.review.protocolIntelligence.saveDraftToastFailure'),
          status: [ToastType.Error],
        })
      );
    },
    [dispatch]
  );

  const {mutateAsync: createProtocol, isPending: isCreating} =
    useCreateProtocolIntelligence();

  const {mutateAsync: createProtocolVersion, isPending: isCreatingVersion} =
    useCreateProtocolVersion();

  const {mutateAsync: updateProtocolVersion, isPending: isUpdating} =
    useUpdateProtocolVersionDetails();

  const handleUpdateSuccess = useCallback(
    (
      data: {protocolId?: number; versionId?: number; protocolVersionId?: number},
      nextStatus: ProtocolStatus
    ) => {
      const {protocolId: nextProtocolId, protocolVersionId: nextVersionId} =
        resolvePersistedProtocolIds(data, protocol, initialProtocol, {
          protocolId: getLocationProtocolId(locationState),
          protocolVersionId: getLocationVersionId(locationState),
        });

      if (nextProtocolId != null && nextVersionId != null) {
        dispatch(
          setProtocolIds({
            protocolId: nextProtocolId,
            protocolVersionId: nextVersionId,
          })
        );
      }
      dispatch(
        setInitialProtocol({
          ...protocol,
          ...(nextProtocolId != null ? {protocolId: nextProtocolId} : {}),
          ...(nextVersionId != null ? {protocolVersionId: nextVersionId} : {}),
          status: nextStatus,
        })
      );
      dispatch(setProtocolStatus(nextStatus));
      if (nextProtocolId != null && nextVersionId != null) {
        onRevisionPersisted?.(
          {protocolId: nextProtocolId, versionId: nextVersionId},
          nextStatus
        );

        if (typeof location.state === 'object' && location.state) {
          navigate(location.pathname, {
            replace: true,
            state: {
              ...(location.state as Record<string, unknown>),
              protocolId: nextProtocolId,
              versionId: nextVersionId,
              protocolVersionId: nextVersionId,
              status: nextStatus,
            },
          });
        } else if (typeof location.state === 'string') {
          navigate(PROTOCOL_INTELLIGENCE_ROUTE, {
            replace: true,
            state: {
              protocolName: trimmedProtocolName || protocol.protocolName.trim(),
              protocolId: nextProtocolId,
              versionId: nextVersionId,
              status: nextStatus,
            },
          });
        }
      }

      if (nextStatus === 'approved') {
        dispatch(
          showToast({
            title: t('common.success'),
            message: t(
              'ai.review.protocolIntelligence.protocolApprovedToastSuccess'
            ),
            status: [ToastType.Success],
          })
        );
        return;
      }

      afterDraftPersisted(
        {navigateBackOnHeader: false},
        nextVersionId ?? protocol.protocolVersionId
      );
    },
    [
      afterDraftPersisted,
      dispatch,
      initialProtocol,
      location.pathname,
      location.state,
      locationState,
      navigate,
      onRevisionPersisted,
      protocol,
      trimmedProtocolName,
    ]
  );

  const submitUpdate = useCallback(
    async (nextStatus: ProtocolStatus) => {
      const versionId =
        protocol.protocolVersionId ?? initialProtocol.protocolVersionId;
      const payload = buildProtocolDirtyPayload(
        {...protocol, protocolVersionId: versionId},
        initialProtocol,
        nextStatus
      );
      if (!payload || submittingStatus !== null) {
        draftSaveSourceRef.current = DRAFT_SAVE_SOURCE.Header;
        return;
      }

      setSubmittingStatus(nextStatus);
      try {
        const response = await updateProtocolVersion(payload);
        handleUpdateSuccess(response, nextStatus);
      } catch {
        draftSaveSourceRef.current = DRAFT_SAVE_SOURCE.Header;
        handleCreateError(nextStatus);
      } finally {
        setSubmittingStatus(null);
      }
    },
    [
      handleCreateError,
      handleUpdateSuccess,
      initialProtocol,
      protocol,
      submittingStatus,
      updateProtocolVersion,
    ]
  );

  const isSubmitting =
    isCreating || isCreatingVersion || isUpdating || submittingStatus !== null;
  // AVR-22771: unsaved revise copies approved content into both working copy and
  // baseline via setInitialProtocol, so hasUnsavedChanges is false until a field
  // is edited. The in-memory new version still needs to be persistable as-is.
  const canSaveDraft =
    Boolean(trimmedProtocolName) &&
    (hasUnsavedChanges || isUnsavedRevision) &&
    !isSavingDisabled &&
    !isSubmitting;
  const canApprove =
    Boolean(trimmedProtocolName) &&
    !isSavingDisabled &&
    !isSubmitting &&
    hasMandatoryApproveFields(protocol);

  const handleCreateVersionSuccess = useCallback(
    (data: ICreateProtocolVersionResponse, nextStatus: ProtocolStatus) => {
      const {protocolId: nextProtocolId, protocolVersionId: nextVersionId} =
        resolvePersistedProtocolIds(data, protocol, initialProtocol, {
          protocolId: getLocationProtocolId(locationState),
          protocolVersionId: getLocationVersionId(locationState),
        });

      if (nextProtocolId != null && nextVersionId != null) {
        dispatch(
          setProtocolIds({
            protocolId: nextProtocolId,
            protocolVersionId: nextVersionId,
          })
        );
      }
      dispatch(setProtocolStatus(nextStatus));
      dispatch(
        setInitialProtocol({
          ...protocol,
          ...(nextProtocolId != null ? {protocolId: nextProtocolId} : {}),
          ...(nextVersionId != null ? {protocolVersionId: nextVersionId} : {}),
          status: nextStatus,
        })
      );
      if (nextProtocolId != null && nextVersionId != null) {
        onRevisionPersisted?.(
          {protocolId: nextProtocolId, versionId: nextVersionId},
          nextStatus
        );
      }

      if (nextStatus === 'draft') {
        afterDraftPersisted(
          {navigateBackOnHeader: false},
          nextVersionId,
          computeNextVersionName(protocolVersions)
        );
        return;
      }

      dispatch(
        showToast({
          title: t('common.success'),
          message: t(
            'ai.review.protocolIntelligence.protocolApprovedToastSuccess'
          ),
          status: [ToastType.Success],
        })
      );
    },
    [
      afterDraftPersisted,
      dispatch,
      initialProtocol,
      locationState,
      onRevisionPersisted,
      protocol,
      protocolVersions,
    ]
  );

  const submitCreateVersion = useCallback(
    async (nextStatus: ProtocolStatus) => {
      const protocolId = protocol.protocolId;
      const parentVersionId = revisionParentVersionId;
      if (
        !trimmedProtocolName ||
        submittingStatus !== null ||
        protocolId == null ||
        parentVersionId == null
      ) {
        draftSaveSourceRef.current = DRAFT_SAVE_SOURCE.Header;
        return;
      }

      setSubmittingStatus(nextStatus);
      try {
        const response = await createProtocolVersion(
          buildCreateProtocolVersionPayload(
            protocolId,
            parentVersionId,
            protocol,
            nextStatus
          )
        );
        handleCreateVersionSuccess(response, nextStatus);
      } catch {
        draftSaveSourceRef.current = DRAFT_SAVE_SOURCE.Header;
        dispatch(
          showToast({
            title: t('common.error'),
            message: t(
              'ai.review.protocolIntelligence.createVersionErrorToast'
            ),
            status: [ToastType.Error],
          })
        );
        onRevisionCreateFailed?.();
      } finally {
        setSubmittingStatus(null);
      }
    },
    [
      createProtocolVersion,
      dispatch,
      handleCreateVersionSuccess,
      onRevisionCreateFailed,
      protocol,
      revisionParentVersionId,
      submittingStatus,
      trimmedProtocolName,
    ]
  );

  const submitCreate = useCallback(
    async (nextStatus: ProtocolStatus) => {
      if (!trimmedProtocolName || submittingStatus !== null) {
        draftSaveSourceRef.current = DRAFT_SAVE_SOURCE.Header;
        return;
      }

      setSubmittingStatus(nextStatus);
      try {
        const response = await createProtocol(
          buildCreateProtocolPayload(protocol, nextStatus)
        );
        handleCreateSuccess(response, nextStatus);
      } catch {
        draftSaveSourceRef.current = DRAFT_SAVE_SOURCE.Header;
        handleCreateError(nextStatus);
      } finally {
        setSubmittingStatus(null);
      }
    },
    [
      createProtocol,
      handleCreateError,
      handleCreateSuccess,
      protocol,
      submittingStatus,
      trimmedProtocolName,
    ]
  );

  const handleCloseModal = () => {
    pendingSwitchVersionIdRef.current = null;
    setPendingPath(null);
    setShowExitModal(false);
  };

  const handleExit = () => {
    if (hasUnsavedChanges) {
      setPendingPath(null);
      setShowExitModal(true);
      return;
    }
    dispatch(resetProtocol());
    navigateToProtocolTabLanding(navigate);
  };

  const persistDraft = useCallback(
    (source: DraftSaveSource) => {
      if (!canSaveDraft) {
        return;
      }

      draftSaveSourceRef.current = source;

      if (isUnsavedRevision) {
        void submitCreateVersion('draft');
        return;
      }

      if (persistedVersionId != null) {
        void submitUpdate('draft');
        return;
      }

      void submitCreate('draft');
    },
    [
      canSaveDraft,
      isUnsavedRevision,
      persistedVersionId,
      submitCreate,
      submitCreateVersion,
      submitUpdate,
    ]
  );

  const handleSaveDraft = useCallback(() => {
    persistDraft(DRAFT_SAVE_SOURCE.Header);
  }, [persistDraft]);

  const handleModalSaveDraft = useCallback(() => {
    persistDraft(DRAFT_SAVE_SOURCE.Exit);
  }, [persistDraft]);

  const handleApprove = useCallback(() => {
    if (!canApprove) {
      return;
    }

    // Unsaved revise → POST create-version with approved
    if (isUnsavedRevision) {
      void submitCreateVersion('approved');
      return;
    }

    // Existing version → approve via dirty PUT (edit API)
    if (persistedVersionId != null) {
      void submitUpdate('approved');
      return;
    }

    // Create flow → POST create with full form + approved
    void submitCreate('approved');
  }, [
    canApprove,
    isUnsavedRevision,
    persistedVersionId,
    submitCreate,
    submitCreateVersion,
    submitUpdate,
  ]);

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const {value} = e.target;
    setProtocolNameValue(value);

    const error = getProtocolNameValidationError(value, namesForDuplicateCheck);
    setValidationError(error);
  };

  const handleNameSave = useCallback(async () => {
    const trimmedValue = protocolNameValue.trim();
    if (!trimmedValue) {
      setProtocolNameValue(originalProtocolName);
      setValidationError(null);
      setIsEditingName(false);
      return;
    }

    const clientError = getProtocolNameValidationError(
      trimmedValue,
      namesForDuplicateCheck,
      true
    );
    if (clientError) {
      setValidationError(clientError);
      return;
    }

    // Unchanged name — skip API and exit edit mode
    if (
      trimmedValue.toLowerCase() === originalProtocolName.trim().toLowerCase()
    ) {
      setProtocolNameValue(originalProtocolName);
      setValidationError(null);
      setIsEditingName(false);
      return;
    }

    if (isSavingName) {
      return;
    }

    const protocolId =
      protocol.protocolId ?? getLocationProtocolId(locationState);

    try {
      const result = await validateName({protocolName: trimmedValue});
      if (isDuplicateNameResponse(result?.isDuplicate)) {
        setValidationError('duplicate');
        return;
      }

      if (protocolId == null) {
        dispatch(
          showToast({
            title: t('common.error'),
            message: t(
              'ai.review.protocolIntelligence.updateProtocolNameErrorToast'
            ),
            status: [ToastType.Error],
          })
        );
        return;
      }

      await updateName({protocolId, protocolName: trimmedValue});

      dispatch(setProtocolName(trimmedValue));
      setOriginalProtocolName(trimmedValue);
      onProtocolNameSave?.(trimmedValue);
      setValidationError(null);
      setIsEditingName(false);
      dispatch(
        showToast({
          title: t('common.success'),
          message: t(
            'ai.review.protocolIntelligence.updateProtocolNameSuccessToast'
          ),
          status: [ToastType.Success],
        })
      );
    } catch (error) {
      const status = getApiErrorStatus(error);
      if (status === 409) {
        setValidationError('duplicate');
        return;
      }

      dispatch(
        showToast({
          title: t('common.error'),
          message:
            status === 403
              ? t(
                  'ai.review.protocolIntelligence.updateProtocolNameAccessDenied'
                )
              : t(
                  'ai.review.protocolIntelligence.updateProtocolNameErrorToast'
                ),
          status: [ToastType.Error],
        })
      );
    }
  }, [
    dispatch,
    isSavingName,
    locationState,
    namesForDuplicateCheck,
    onProtocolNameSave,
    originalProtocolName,
    protocol.protocolId,
    protocolNameValue,
    updateName,
    validateName,
  ]);

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      void handleNameSave();
    }
    if (e.key === 'Escape') {
      setProtocolNameValue(originalProtocolName);
      setValidationError(null);
      setIsEditingName(false);
    }
  };

  const handleNameClear = () => {
    setProtocolNameValue(originalProtocolName);
    setValidationError(null);
    setIsEditingName(false);
  };

  const handleNameBlur = () => {
    void handleNameSave();
  };

  const renderProtocolName = () => {
    if (isApproved && isEditingName) {
      return (
        <Box className="protocol-name-field">
          <ArrTextField
            size="medium"
            sizeVariant="medium"
            inputRef={inputRef}
            aria-label={t('ai.review.protocolIntelligence.protocolNameLabel')}
            value={protocolNameValue}
            onChange={handleNameChange}
            onBlur={handleNameBlur}
            onKeyDown={handleNameKeyDown}
            state={
              validationError ? 'error' : isSavingName ? 'inactive' : 'enabled'
            }
            showErrorIcon={Boolean(validationError)}
            supportText={helperText}
            helperText={helperText}
            dataTestId="protocol-name-input-field"
            slotProps={{
              input: {
                inputProps: {maxLength: PROTOCOL_NAME_MAX_LENGTH},
              },
              formHelperText: {
                'aria-label': validationError
                  ? getProtocolNameValidationMessage(validationError)
                  : `${protocolNameValue.length} ${t('ai.review.jobDetailsTab.saveAsNewProtocolOutOf50Characters')}`,
              },
            }}
          />
          {Boolean(protocolNameValue) && (
            <IconButton
              className="clear-icon"
              data-testid="clear-icon"
              aria-label={t('common.cancel')}
              onMouseDown={(e) => {
                // Prevent input blur so cancel does not save the edited name
                e.preventDefault();
              }}
              onClick={handleNameClear}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      );
    }

    return (
      <>
        <Typography
          component="h1"
          className="typo-text subhead1_size"
          tabIndex={0}
          aria-label={t('ai.review.protocolIntelligence.protocolNameLabel')}
          data-testid="protocol-name"
        >
          {protocolNameValue}
        </Typography>
        {isApproved && (
          <IconButton
            className="icon-button"
            aria-label={t('ai.review.protocolIntelligence.editProtocolName', {
              protocolName: protocolNameValue,
            })}
            data-testid="edit-protocol-name"
            onClick={() => setIsEditingName(true)}
          >
            <EditOutlinedIcon className="svg-edit-icon" fontSize="medium" />
          </IconButton>
        )}
      </>
    );
  };

  return (
    <Box component="header" className="protocol-header">
      <Box className="protocol-header-exit-button">
        <ArrButton
          variant="text"
          size="small"
          aria-label={t('ai.review.protocolIntelligence.exitLabel')}
          onClick={handleExit}
          dataTestId="exit-button"
          disabled={isSubmitting}
        >
          <NavigateBeforeIcon fontSize="small" className="button-arrow" />
          {t('ai.review.protocolIntelligence.exitLabel')}
        </ArrButton>
        <Box className="protocol-name">{renderProtocolName()}</Box>
      </Box>

      <Box
        className={`action-button ${isApproved && isEditingName && `action-button-spacing`}`}
        role="group"
        aria-label={t('ai.review.protocolIntelligence.protocolActionsLabel')}
      >
        {isApproved ? (
          <>
            <ArrButton
              variant="secondary"
              size="medium"
              aria-label={t('ai.review.protocolIntelligence.versionHistory')}
              dataTestId="version-history"
              className="protocol-header-action-button"
              onClick={onVersionHistoryClick}
            >
              {t('ai.review.protocolIntelligence.versionHistory')}
            </ArrButton>
            <ArrButton
              variant="primary"
              size="medium"
              aria-label={t('ai.review.protocolIntelligence.revise')}
              dataTestId="revise-protocol"
              className="protocol-header-action-button"
              onClick={onReviseClick}
            >
              {t('ai.review.protocolIntelligence.revise')}
            </ArrButton>
          </>
        ) : (
          <>
            {trailingAction}
            {isUnsavedRevision && (
              <ArrButton
                variant="secondary"
                size="medium"
                aria-label={t('ai.review.protocolIntelligence.cancelRevision')}
                dataTestId="cancel-revision"
                className="protocol-header-action-button"
                aria-disabled={isSubmitting}
                isAriaDisabled={isSubmitting}
                disabled={isSubmitting}
                onClick={onUnsavedRevisionCancel}
              >
                {t('ai.review.protocolIntelligence.cancelRevision')}
              </ArrButton>
            )}
            <ArrButton
              variant="secondary"
              size="medium"
              aria-label={t('ai.review.protocolIntelligence.saveDraft')}
              dataTestId="save-as-draft"
              className="job-result-header-download-button"
              aria-disabled={!canSaveDraft}
              isAriaDisabled={!canSaveDraft}
              disabled={!canSaveDraft}
              loading={submittingStatus === 'draft'}
              onClick={handleSaveDraft}
            >
              {t('ai.review.protocolIntelligence.saveDraft')}
            </ArrButton>
            <ArrButton
              variant="primary"
              size="medium"
              aria-label={t('ai.review.protocolIntelligence.approve')}
              dataTestId="approve"
              className="job-result-header-download-button"
              aria-disabled={!canApprove}
              isAriaDisabled={!canApprove}
              disabled={!canApprove}
              loading={submittingStatus === 'approved'}
              onClick={handleApprove}
            >
              {t('ai.review.protocolIntelligence.approve')}
            </ArrButton>
          </>
        )}
      </Box>

      <ExitProtocolModal
        open={showExitModal && hasUnsavedChanges}
        onClose={handleCloseModal}
        pendingPath={pendingPath}
        pendingSwitchVersionId={resolvedSwitchVersionId}
        onDiscard={onExitDiscard}
        onSaveDraft={handleModalSaveDraft}
        canSaveDraft={canSaveDraft}
        isSaving={submittingStatus === 'draft'}
      />
    </Box>
  );
};

export default ProtocolHeader;
